<template>
  <v-app class="app-main">
    <header-series v-if="!isFullscreen" />
    <div class="reader-container" :class="{ 'fullscreen-mode': isFullscreen }">
      <div class="aligner">
        <div class="chapter-header" v-if="!isFullscreen">
          <h2 class="title-chapter">{{ chapter.name }}</h2>
          <v-select v-model="readingMode" :items="['Vertical', 'Horizontal']" label="Modo de Leitura" dark dense
            outlined class="reading-mode-selector" />
          <v-select v-if="readingMode === 'Horizontal'" v-model="pageDisplayMode" :items="['Single Page', 'Dual Page']"
            label="Páginas por Tela" dark dense outlined class="page-display-selector" />
          <v-btn @click="toggleFullscreen" class="fullscreen-btn" title="Tela cheia">
            TELA CHEIA
            <v-icon color="#e9e4e4">mdi-fullscreen</v-icon>
          </v-btn>
        </div>

        <!-- Modo de leitura VERTICAL -->
        <div v-if="readingMode === 'Vertical'" class="read-vertical">
          <div class="image-container" ref="imageContainer">
            <img v-for="(page, index) in chapter.pagesData" :key="index" :src="page.image"
              :alt="'Página ' + page.page + ' do capítulo ' + chapter.name" @contextmenu.prevent class="responsive-image" />
          </div>
        </div>

        <!-- Modo de leitura HORIZONTAL -->
        <div v-else class="read-horizontal">
          <v-carousel v-model="currentPageIndex" hide-delimiters show-arrows :height="carouselHeight"
            :continuous="false" @change="handleCarouselChange" touch>
            <v-carousel-item v-for="(pagePair, index) in pairedPages" :key="index">
              <div class="page-pair-container" :class="{ 'dual-page': pageDisplayMode === 'Dual Page' }">
                <img :src="pagePair[0].image" :alt="'Página ' + pagePair[0].page + ' do capítulo ' + chapter.name" @contextmenu.prevent
                  class="carousel-image" />
                <img v-if="pagePair[1]" :src="pagePair[1].image"
                  :alt="'Página ' + pagePair[1].page + ' do capítulo ' + chapter.name" @contextmenu.prevent class="carousel-image" />
              </div>
            </v-carousel-item>
          </v-carousel>
        </div>

        <widget-read-menu v-if="!isFullscreen"
          :next_chapter="chapter.next_chapter"
          :previous_title="chapter.previous_title"
          :idObra="chapter.project_id"
          :idChapter="idChapter"
          :chapter="chapter"
          @update-chapter="fetchChapter" />
      </div>
    </div>
    <CommentsChapter v-if="!isFullscreen" :idChapter="idChapter" :key="idChapter" :is-admin="isAdmin" />

    <!-- Snackbar para notificar o fim do capítulo -->
    <v-snackbar v-model="showEndSnackbar" color="info" timeout="3000" top>
      Você chegou ao final do capítulo!
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="showEndSnackbar = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import WidgetReadMenu from "@/components/WidgetReadMenu.vue";
import CommentsChapter from "@/components/CommentsChapter.vue";
import TitleService from "@/server/TitleService";
import { isAdminModeActive } from "@/utils/adminMode";

export default {
  name: "ObraPageView",
  props: ["idChapter"],
  components: {
    WidgetReadMenu,
    CommentsChapter,
  },
  data: () => ({
    chapter: {
      id: null,
      name: "",
      pagesData: [],
      next_chapter: null,
      previous_title: null,
    },
    readingMode: "Vertical",
    pageDisplayMode: "Single Page",
    currentPageIndex: 0,
    service: new TitleService(),
    showEndSnackbar: false,
    isFullscreen: false,
    isAdmin: false,
  }),

  mounted() {
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    this.isAdmin = isAdminModeActive();
  },

  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
  },

  watch: {
    idChapter: {
      immediate: true,
      handler() {
        this.fetchChapter();
      },
    },
    readingMode(newMode) {
      this.currentPageIndex = 0;
      this.showEndSnackbar = false;
      if (newMode === "Horizontal" && window.innerWidth <= 768) {
        this.pageDisplayMode = "Single Page";
      } else if (newMode === "Horizontal") {
        this.pageDisplayMode = "Single Page";
      }
    },
    pageDisplayMode() {
      this.currentPageIndex = 0;
      this.showEndSnackbar = false;
    },
    isFullscreen(newVal) {
      if (newVal && this.readingMode === 'Vertical') {
        this.$nextTick(() => {
          setTimeout(() => {
            const imageContainer = this.$refs.imageContainer;
            if (imageContainer) {
              console.log('Resetting scroll for vertical fullscreen mode');
              imageContainer.scrollTop = 0;
              imageContainer.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              console.error('Image container not found');
            }
          }, 200);
        });
      }
    },
  },

  computed: {
    carouselHeight() {
      return window.innerWidth <= 768 && this.pageDisplayMode === "Dual Page" ? "85vh" : "100vh";
    },
    pairedPages() {
      const pairs = [];
      if (this.readingMode === "Horizontal" && this.pageDisplayMode === "Single Page") {
        this.chapter.pagesData.forEach((page) => {
          pairs.push([page, null]);
        });
      } else {
        for (let i = 0; i < this.chapter.pagesData.length; i += 2) {
          pairs.push([
            this.chapter.pagesData[i],
            this.chapter.pagesData[i + 1] || null,
          ]);
        }
      }
      return pairs;
    },
  },

  methods: {
    toggleFullscreen() {
      const el = document.documentElement;
      if (!document.fullscreenElement) {
        el.requestFullscreen?.();
        this.isFullscreen = true;
      } else {
        document.exitFullscreen?.();
        this.isFullscreen = false;
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    async fetchChapter() {
      try {
        const response = await this.service.view(this.idChapter);
        this.chapter = {
          ...response,
          pagesData: (response.pages_data || []).sort((a, b) => a.page - b.page),
          next_chapter: response.next_title ? response.next_title.id : null,
          previous_title: response.previous_title ? response.previous_title.id : null,
        };
        this.currentPageIndex = 0;
        this.showEndSnackbar = false;
        const viewResult = await this.service.recordView(this.idChapter);
        if (viewResult?.views !== undefined) {
          this.chapter.views = viewResult.views;
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Erro ao carregar páginas do capítulo:", error);
      }
    },
    handleCarouselChange(newIndex) {
      if (newIndex === this.pairedPages.length - 1) {
        this.showEndSnackbar = true;
      }
    },
  },
};
</script>

<style scoped>
.fullscreen-btn {
  background-color: var(--zinnes-primary) !important;
  padding: 2px 4px;
  color: #e9e4e4 !important;
}

.title-chapter {
  font-size: 36px;
  font-family: 'Bangers', cursive;
  letter-spacing: 2px;
  color: white;
  text-align: center;
  margin-bottom: 20px;
}

.reader-container {
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: 10px;
  box-shadow: none;
}

.chapter-header {
  color: white;
  text-align: center;
  margin-bottom: 20px;
}

.reading-mode-selector,
.page-display-selector {
  max-width: 200px;
  margin: 10px auto;
}

.read-vertical .image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 0;
}

.read-vertical .responsive-image {
  width: 70%;
  height: auto;
  max-width: 1200px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.page-pair-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  gap: 2px;
  overflow: hidden;
}

.carousel-image {
  height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  background: black;
  z-index: 9999;
}

.fullscreen-mode .read-vertical {
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.fullscreen-mode .read-vertical .image-container {
  width: 100vw;
  height: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  overflow-y: visible;
  position: relative;
}

.fullscreen-mode .read-vertical .responsive-image {
  width: 100vw;
  height: auto;
  max-height: 100vh;
  object-fit: contain;
  margin: 0;
  flex-shrink: 0;
}

.fullscreen-mode .read-horizontal {
  width: 100vw;
  height: 100vh;
}

.fullscreen-mode .read-horizontal .v-carousel {
  width: 100vw;
  height: 100vh;
}

.fullscreen-mode .pagestill-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0;
}

.fullscreen-mode .carousel-image {
  width: 100vw;
  height: auto;
  max-height: 100vh;
  object-fit: contain;
}

.fullscreen-mode .page-pair-container.dual-page .carousel-image {
  width: 40vw;
  height: auto;
  max-height: 100vh;
  object-fit: contain;
}

@media (min-width: 1920px) {
  .read-vertical .responsive-image {
    max-width: 1600px;
  }
}

@media (max-width: 768px) {
  .reader-container {
    width: 100%;
    padding: 5px;
  }

  .reading-mode-selector,
  .page-display-selector {
    max-width: 100%;
    margin: 5px auto;
  }

  .read-vertical .responsive-image {
    max-width: 100%;
  }

  .carousel-image {
    height: 100%;
    width: auto;
  }

  .page-pair-container.dual-page .carousel-image {
    max-width: 48%;
    object-fit: contain;
  }

  .title-chapter {
    font-size: 16px;
  }
}

@media (min-width: 1024px) {
  .read-vertical .responsive-image {
    max-width: 1500px;
  }
}

@media (max-width: 425px) {
  .read-vertical .responsive-image {
  width: 95%;
  height: auto;
  max-width: 1200px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}
}
</style>